// Core Imports
import { api } from '@/core/lib/api'
import type { TResponse } from '@/core/types/axios'

// External Module Imports
import type { TPermission } from '@/modules/role/role.types'
import { normalizePermissionKey } from '@/modules/role/role.utils'

// Module Imports
import { authEndpoints } from '@/modules/auth/auth.constants'
import type {
  TAuthRole,
  TForgotPasswordRequest,
  TResetPasswordRequest,
  TSignInRequest,
  TSignInResponse,
  TThemeName,
  TUser,
  TValidateSocialSignInRequest,
} from '@/modules/auth/auth.types'

const mapTheme = (theme: unknown): TThemeName =>
  theme === 'canli' ? 'canli' : 'klasik'

type TPermissionResponse = {
  id?: unknown
  name?: unknown
  description?: unknown
}

type TRoleResponse = {
  id?: unknown
  name?: unknown
  description?: unknown
  permissions?: unknown
}

type TAuthUserResponse = {
  id?: unknown
  name?: unknown
  email?: unknown
  theme?: unknown
  role?: unknown
  roles?: unknown
  permissions?: unknown
  created_at?: unknown
  updated_at?: unknown
}

type TSignInUserResponse = TAuthUserResponse & {
  access_token?: unknown
}

const mapPermission = (permission: TPermissionResponse): TPermission | null => {
  const normalizedName = normalizePermissionKey(permission?.name)

  if (typeof permission?.id !== 'string' || !normalizedName) {
    return null
  }

  return {
    id: permission.id,
    name: normalizedName,
    description:
      typeof permission.description === 'string' ? permission.description : '',
  }
}

const mapRole = (role: TRoleResponse): TAuthRole | null => {
  if (typeof role?.id !== 'string' || typeof role?.name !== 'string') {
    return null
  }

  const permissions = Array.isArray(role.permissions)
    ? role.permissions
        .map((permission) => mapPermission(permission as TPermissionResponse))
        .filter((permission): permission is TPermission => !!permission)
    : []

  return {
    id: role.id,
    name: role.name,
    description: typeof role.description === 'string' ? role.description : '',
    permissions,
  }
}

const getFlatPermissions = (
  roles: TAuthRole[],
  responsePermissions: unknown
) => {
  if (Array.isArray(responsePermissions)) {
    return responsePermissions
      .map((permission) => mapPermission(permission as TPermissionResponse))
      .filter((permission): permission is TPermission => !!permission)
      .map((permission) => permission.name)
  }

  return Array.from(
    new Set(
      roles.flatMap((role) =>
        role.permissions.map((permission) => permission.name)
      )
    )
  )
}

const mapAuthUser = (user: TAuthUserResponse): TUser => {
  const roles = Array.isArray(user.roles)
    ? user.roles
        .map((role) => mapRole(role as TRoleResponse))
        .filter((role): role is TAuthRole => !!role)
    : []

  return {
    id: typeof user.id === 'string' ? user.id : '',
    name: typeof user.name === 'string' ? user.name : '',
    email: typeof user.email === 'string' ? user.email : '',
    theme: mapTheme(user.theme),
    role: typeof user.role === 'string' ? user.role : null,
    roles,
    permissions: getFlatPermissions(roles, user.permissions),
    created_at: typeof user.created_at === 'string' ? user.created_at : '',
    updated_at: typeof user.updated_at === 'string' ? user.updated_at : '',
  }
}

const mapSignInUser = (user: TSignInUserResponse): TSignInResponse => {
  return {
    ...mapAuthUser(user),
    access_token:
      typeof user.access_token === 'string' ? user.access_token : '',
  }
}

export async function authUser(): Promise<TUser> {
  const response = await api.get<TResponse<TAuthUserResponse>>(
    authEndpoints.authUser
  )

  return mapAuthUser(response.data.data ?? {})
}

export async function signIn(
  payload: TSignInRequest
): Promise<TSignInResponse> {
  const response = await api.post<TResponse<TSignInUserResponse>>(
    authEndpoints.signIn,
    payload
  )

  return mapSignInUser(response.data.data ?? {})
}

export async function validateSocialSignIn(
  payload: TValidateSocialSignInRequest
): Promise<TSignInResponse> {
  const response = await api.post<TResponse<TSignInUserResponse>>(
    authEndpoints.validateSocialSignIn,
    payload
  )

  return mapSignInUser(response.data.data ?? {})
}

export async function forgotPassword(
  payload: TForgotPasswordRequest
): Promise<void> {
  await api.put(authEndpoints.forgotPassword, payload)
}

export async function resetPassword(
  payload: TResetPasswordRequest
): Promise<void> {
  await api.put(authEndpoints.resetPassword, payload)
}

export async function signOut(): Promise<void> {
  await api.get<void>(authEndpoints.signOut)
}
